import { createContext, useContext } from 'react';
import type { AuthState } from './auth-reducer';
import type { AuthChallenge } from './cognito';
import type { AuthSession } from './types';

export type SignInOutcome =
  | { status: 'signed-in' }
  | { status: 'failed' }
  | { status: 'challenge'; challenge: AuthChallenge };

export interface AuthContextValue extends AuthState {
  /** Resolves `signed-in` when the operator is in, `failed` when the attempt
   * was refused (the message lands on `error`), and `challenge` when Cognito
   * wants a new password or an authenticator code. The password never
   * leaves the sign-in form. `appName` is the issuer an authenticator app
   * lists the account under. */
  signIn: (email: string, password: string, appName: string) => Promise<SignInOutcome>;
  /** Stores the session a finished challenge step produced. */
  completeSignIn: (session: AuthSession) => void;
  signOut: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return context;
}
